import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Alert,
  Snackbar,
  CircularProgress,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import { api } from '../services/api';

interface ClientEstimate {
  id: number;
  case_id: number;
  client_firm_id: number;
  billing_type: string;
  rate: number;
  quantity: number;
  total_amount: number;
  description: string;
  status: 'draft' | 'pending' | 'approved' | 'rejected';
  created_by: number | null;
  approved_by: number | null;
  approved_at: string | null;
  rejection_reason: string | null;
  is_invoiced: boolean;
  created_at: string;
  clientFirm: {
    id: number;
    name: string;
  };
  createdBy?: {
    id: number;
    name: string;
  };
  approvedBy?: {
    id: number;
    name: string;
  };
}

interface User {
  id: number;
  name: string;
  email: string;
  roles?: Array<{ slug: string; name: string }>;
}

interface ClientEstimationProps {
  caseId: number;
  projectId: number;
  user: User | null;
}

const ClientEstimation: React.FC<ClientEstimationProps> = ({ 
  caseId, 
  projectId, 
  user 
}) => {
  const [estimates, setEstimates] = useState<ClientEstimate[]>([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [editingEstimate, setEditingEstimate] = useState<ClientEstimate | null>(null);
  const [projectSettings, setProjectSettings] = useState<any>(null);
  
  const [formData, setFormData] = useState({
    quantity: '',
    description: '',
  });

  const [showRejectDialog, setShowRejectDialog] = useState(false);
  const [rejectingEstimate, setRejectingEstimate] = useState<ClientEstimate | null>(null);
  const [rejectionReason, setRejectionReason] = useState('');

  const isPmpAdmin = () => {
    if (!user?.roles) return false;
    return user.roles.some((role) => role.slug === 'pmp_admin');
  };

  const isSiteAdmin = () => {
    if (!user?.roles) return false;
    return user.roles.some((role) => role.slug === 'site_admin');
  };

  const isPmpAdminOrSiteAdmin = () => {
    return isPmpAdmin() || isSiteAdmin();
  };

  const isClientFirmUser = (estimate: ClientEstimate) => {
    if (!user?.roles) return false;
    const clientRoles = ['firm_admin', 'firm_main'];
    return user.roles.some((role) => clientRoles.includes(role.slug));
  };

  useEffect(() => {
    loadEstimates();
    loadProjectSettings();
  }, [caseId, projectId]);

  const loadEstimates = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/cases/${caseId}/client-estimates`);
      if (response.data.success) {
        setEstimates(response.data.data);
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load client estimates');
    } finally {
      setLoading(false);
    }
  };

  const loadProjectSettings = async () => {
    try {
      const response = await api.get(`/projects/${projectId}/settings`);
      if (response.data.success) {
        setProjectSettings(response.data.data);
      }
    } catch (err: any) {
      // Silently fail - project settings are optional
      console.error('Failed to load project settings:', err);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.quantity) {
      setError('Please enter quantity');
      return;
    }

    try {
      setSubmitting(true);
      
      const payload = {
        quantity: parseInt(formData.quantity),
        description: formData.description,
      };

      const response = editingEstimate
        ? await api.put(`/client-estimates/${editingEstimate.id}/draft`, payload)
        : await api.post(`/cases/${caseId}/client-estimates`, payload);

      if (response.data.success) {
        setSuccess(editingEstimate ? 'Estimate updated successfully' : 'Estimate created successfully');
        setFormData({ quantity: '', description: '' });
        setEditingEstimate(null);
        loadEstimates();
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to save estimate');
    } finally {
      setSubmitting(false);
    }
  };

  const handleEditDraft = (estimate: ClientEstimate) => {
    setEditingEstimate(estimate);
    setFormData({
      quantity: estimate.quantity.toString(),
      description: estimate.description || '',
    });
  };

  const handleCancelEdit = () => {
    setEditingEstimate(null);
    setFormData({ quantity: '', description: '' });
  };

  const handleSubmitForApproval = async (estimate: ClientEstimate) => {
    try {
      const response = await api.post(`/client-estimates/${estimate.id}/submit`);
      if (response.data.success) {
        setSuccess('Estimate submitted for client approval');
        loadEstimates();
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to submit estimate');
    }
  };

  const handleApprove = async (estimate: ClientEstimate) => {
    try {
      const response = await api.put(`/client-estimates/${estimate.id}/status`, {
        action: 'approve'
      });
      if (response.data.success) {
        setSuccess('Estimate approved successfully');
        loadEstimates();
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to approve estimate');
    }
  };

  const handleRejectClick = (estimate: ClientEstimate) => {
    setRejectingEstimate(estimate);
    setRejectionReason('');
    setShowRejectDialog(true);
  };

  const handleRejectConfirm = async () => {
    if (!rejectingEstimate || !rejectionReason.trim()) {
      setError('Please provide a rejection reason');
      return;
    }

    try {
      const response = await api.put(`/client-estimates/${rejectingEstimate.id}/status`, {
        action: 'reject',
        rejection_reason: rejectionReason
      });
      if (response.data.success) {
        setSuccess('Estimate rejected successfully');
        setShowRejectDialog(false);
        setRejectingEstimate(null);
        setRejectionReason('');
        loadEstimates();
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to reject estimate');
    }
  };

  const getBillingTypeLabel = (type: string) => {
    const labels: Record<string, string> = {
      page_based: 'Page Based',
      time_based: 'Time Based',
      case_based: 'Case Based',
      flat_rate: 'Flat Rate',
    };
    return labels[type] || type;
  };

  const getQuantityLabel = (type: string) => {
    const labels: Record<string, string> = {
      page_based: 'pages',
      time_based: 'hours',
      case_based: 'cases',
      flat_rate: 'pages',
    };
    return labels[type] || 'units';
  };

  const getStatusColor = (status: string) => {
    const colors: Record<string, string> = {
      draft: 'default',
      pending: 'warning',
      approved: 'success',
      rejected: 'error',
    };
    return colors[status] || 'default';
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Client Billing Estimate
        </Typography>
        <Divider sx={{ mb: 2 }} />

        {/* Show billing type information from project settings */}
        {projectSettings?.custom_fields?.billing_type && (
          <Alert severity="info" sx={{ mb: 2 }}>
            <Typography variant="subtitle2" gutterBottom>
              <strong>Billing Type:</strong> {projectSettings.custom_fields.billing_type}
            </Typography>
            {projectSettings.custom_fields.billing_type === 'Flat Rate' && (
              <Box sx={{ mt: 1 }}>
                <Typography variant="body2">
                  <strong>Flat Rate:</strong> ${projectSettings.custom_fields.flat_rate || 0} 
                  {' '}for pages {projectSettings.custom_fields.flat_rate_page_from || 0} - {projectSettings.custom_fields.flat_rate_page_to || 0}
                </Typography>
                {projectSettings.custom_fields.variable_rate && (
                  <Typography variant="body2" sx={{ mt: 0.5 }}>
                    <strong>Variable Rate:</strong> ${projectSettings.custom_fields.variable_rate} per page 
                    {projectSettings.custom_fields.variable_rate_page_from && (
                      <> for pages {projectSettings.custom_fields.variable_rate_page_from}+</>
                    )}
                    {projectSettings.custom_fields.variable_rate_page_to && (
                      <> (up to {projectSettings.custom_fields.variable_rate_page_to})</>
                    )}
                  </Typography>
                )}
                <Typography variant="body2" sx={{ mt: 1, fontStyle: 'italic' }}>
                  Calculation: If pages ≤ {projectSettings.custom_fields.flat_rate_page_to || 0}, total = ${projectSettings.custom_fields.flat_rate || 0}. 
                  If pages &gt; {projectSettings.custom_fields.flat_rate_page_to || 0}, total = ${projectSettings.custom_fields.flat_rate || 0} + (${projectSettings.custom_fields.variable_rate || 0} × pages above {projectSettings.custom_fields.flat_rate_page_to || 0}).
                </Typography>
              </Box>
            )}
            {projectSettings.custom_fields.billing_type === 'Page Based' && projectSettings.default_page_rate && (
              <Typography variant="body2" sx={{ mt: 1 }}>
                <strong>Rate:</strong> ${projectSettings.default_page_rate} per page
              </Typography>
            )}
            {projectSettings.custom_fields.billing_type === 'Time Based' && projectSettings.default_hourly_rate && (
              <Typography variant="body2" sx={{ mt: 1 }}>
                <strong>Rate:</strong> ${projectSettings.default_hourly_rate} per hour
              </Typography>
            )}
            {projectSettings.custom_fields.billing_type === 'Case Based' && projectSettings.custom_fields.rate && (
              <Typography variant="body2" sx={{ mt: 1 }}>
                <strong>Rate:</strong> ${projectSettings.custom_fields.rate} per case
              </Typography>
            )}
          </Alert>
        )}

        {isPmpAdminOrSiteAdmin() && (
          <form onSubmit={handleSubmit}>
            <TextField
              fullWidth
              label="Quantity"
              type="number"
              value={formData.quantity}
              onChange={(e) => setFormData({ ...formData, quantity: e.target.value })}
              margin="normal"
              required
              helperText="Enter the quantity (pages, hours, or cases based on project settings)"
            />

            <TextField
              fullWidth
              label="Description"
              multiline
              rows={3}
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              margin="normal"
              helperText="Optional description for this estimate"
            />

            <Box sx={{ mt: 2, display: 'flex', gap: 2 }}>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                disabled={submitting}
              >
                {submitting ? <CircularProgress size={24} /> : (editingEstimate ? 'Update Draft' : 'Save as Draft')}
              </Button>
              {editingEstimate && (
                <Button
                  variant="outlined"
                  onClick={handleCancelEdit}
                >
                  Cancel
                </Button>
              )}
            </Box>
          </form>
        )}

        {!isPmpAdmin() && (
          <Alert severity="info">
            Client estimates are created by PMP administrators.
          </Alert>
        )}
      </Paper>

      <Paper elevation={2} sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom>
          Estimate History
        </Typography>
        <Divider sx={{ mb: 2 }} />

        {estimates.length === 0 ? (
          <Typography color="text.secondary">No estimates found for this case.</Typography>
        ) : (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Client Firm</TableCell>
                  <TableCell>Billing Type</TableCell>
                  <TableCell>Quantity</TableCell>
                  <TableCell>Description</TableCell>
                  <TableCell>Rate</TableCell>
                  <TableCell>Total Amount</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Created Date</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {estimates.map((estimate) => (
                  <TableRow key={estimate.id}>
                    <TableCell>
                      <Typography variant="body2" fontWeight="bold">
                        {estimate.clientFirm?.name || 'Unknown Firm'}
                      </Typography>
                    </TableCell>
                    <TableCell>{getBillingTypeLabel(estimate.billing_type)}</TableCell>
                    <TableCell>
                      {estimate.quantity} {getQuantityLabel(estimate.billing_type)}
                    </TableCell>
                    <TableCell>
                      {estimate.description || '—'}
                    </TableCell>
                    <TableCell>${Number(estimate.rate).toFixed(2)}</TableCell>
                    <TableCell>${Number(estimate.total_amount).toFixed(2)}</TableCell>
                    <TableCell>
                      <Chip
                        label={estimate.status.toUpperCase()}
                        color={getStatusColor(estimate.status) as any}
                        size="small"
                      />
                      {estimate.is_invoiced && (
                        <Chip
                          label="INVOICED"
                          color="info"
                          size="small"
                          sx={{ ml: 1 }}
                        />
                      )}
                    </TableCell>
                    <TableCell>
                      {new Date(estimate.created_at).toLocaleDateString()}
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        {/* PMP Admin actions for draft */}
                        {isPmpAdmin() && estimate.status === 'draft' && (
                          <>
                            <Button
                              size="small"
                              variant="outlined"
                              onClick={() => handleEditDraft(estimate)}
                            >
                              Edit
                            </Button>
                            <Button
                              size="small"
                              variant="contained"
                              color="primary"
                              onClick={() => handleSubmitForApproval(estimate)}
                            >
                              Submit for Approval
                            </Button>
                          </>
                        )}

                        {/* Client firm user actions for pending */}
                        {estimate.status === 'pending' && (isPmpAdmin() || isClientFirmUser(estimate)) && (
                          <>
                            <Button
                              size="small"
                              variant="contained"
                              color="success"
                              onClick={() => handleApprove(estimate)}
                            >
                              Approve
                            </Button>
                            <Button
                              size="small"
                              variant="contained"
                              color="error"
                              onClick={() => handleRejectClick(estimate)}
                            >
                              Reject
                            </Button>
                          </>
                        )}

                        {estimate.status === 'rejected' && estimate.rejection_reason && (
                          <Typography variant="caption" color="error">
                            {estimate.rejection_reason}
                          </Typography>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      {/* Reject Dialog */}
      <Dialog open={showRejectDialog} onClose={() => setShowRejectDialog(false)}>
        <DialogTitle>Reject Estimate</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Rejection Reason"
            multiline
            rows={4}
            value={rejectionReason}
            onChange={(e) => setRejectionReason(e.target.value)}
            margin="normal"
            required
            helperText="Please provide a reason for rejecting this estimate"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowRejectDialog(false)}>Cancel</Button>
          <Button onClick={handleRejectConfirm} variant="contained" color="error">
            Reject Estimate
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={!!success}
        autoHideDuration={6000}
        onClose={() => setSuccess(null)}
      >
        <Alert severity="success" onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      </Snackbar>

      <Snackbar
        open={!!error}
        autoHideDuration={6000}
        onClose={() => setError(null)}
      >
        <Alert severity="error" onClose={() => setError(null)}>
          {error}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ClientEstimation;

